import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, input } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import saveAs from "@shared/utils/fileSaver.js";
import { Observable, Subject, forkJoin, of } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";

import { ToastService } from "@shared/services/toast/toast.service";
import { FileErorrDialogComponent } from "@modules/file-management/_components/file-error-dialog/file-erorr-dialog/file-erorr-dialog.component";
import { UserService } from "@modules/user-management/_services/user.service";
import { Milestone, PayCycle } from "src/app/shared/models/pay-cycle.interface";
import { FileManagementUploadService } from "../../../../../shared/services/file-management/file-management-upload.service";
import { FileManagementService } from "../../../../../shared/services/file-management/file-management.service";
import {
	FileHistory,
	FileHistoryPagination,
	FileManagerPlaceholdersPagination,
	FileTypes
} from "../../../../../shared/models/file-manager";
import { PayGroup } from "../../../../../shared/models/pay-groups";
import { TppPaymentsApiService } from "../../../services/tpp-payments-api.service";
import { cancelReport, cancelReportWithPagination } from "../../../tpp-payments/models/tpp-payment.interface";
import { TaskCompleteDialogComponent } from "../_components/task-complete-dialog/task-complete-dialog.component";
import { PaymentOrderHistoryLog } from "@shared/models/payment-order-history-log.interface";

@Component({
	selector: "app-file-list",
	templateUrl: "./file-list.component.html",
	styleUrls: ["./file-list.component.scss"]
})
export class FileListComponent implements OnInit, OnDestroy {
	payGroup = input<PayGroup>();
	payCycleExternalId = input("");
	payrollCycleId = input("");
	payCycle = input<PayCycle>();
	milestone = input<Milestone>();

	@Output() onFileClick = new EventEmitter<null>();
	@Output() cpNowFileCLick = new EventEmitter<null>();
	@Output() resetStatus: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() cancelStatus: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() totalFileHistoryCount: EventEmitter<number> = new EventEmitter<number>();
	@Output() currentFileHistoryCount: EventEmitter<number> = new EventEmitter<number>();
	@Output() onViewHistoryLogClick = new EventEmitter<any>();

	resetHistoryFormat$!: PayCycle;
	files$!: Observable<FileHistory[]>;
	history$!: Observable<FileHistory[]>;
	cancelReports!: cancelReport[];
	payCycle$!: Observable<PayCycle>;
	destroy$ = new Subject<void>();
	canUploadFiles = false;
	lastResetIndex = 10;
	disableIntegrationButton = true;
	defaultFileHistoryCount = 20;
	fileHistoryCount = this.defaultFileHistoryCount;
	isFetching = true;
	readonly Object = Object;
	filesHistory!: FileHistory;
	paymentOrderDropdown = false;
	isEnabledCPNDeductionUpload = false;
	isEnabledG2NFileUpload = true;
	isPaymentDeductionEnabled = false;
	fileHistoryItems!: FileHistory[];
	hasFailedOrErrorFile = false;

	public dialog = inject(MatDialog);
	public userService = inject(UserService);
	private fileManagerApiService = inject(FileManagementService);
	private commonFileManagerService = inject(FileManagementUploadService);
	private tppPaymentsApiService = inject(TppPaymentsApiService);
	private toastService = inject(ToastService);

	ngOnInit(): void {
		this.history$ = this.getFiles().pipe(take(1));
		this.commonFileManagerService.updateFileList$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.history$ = this.getFiles().pipe(take(1));
		});

		this.isPaymentDeductionEnabled = !!this.payGroup()?.data?.cpNowDeductionsEnabled;
		this.isEnabledCPNDeductionUpload = this.milestone()?.status === "COMPLETE";
		this.history$.subscribe((history: FileHistory[]) => {
			this.hasFailedOrErrorFile = history?.length
				? history.some(f => f.status === "FAILED" || f.status === "ERROR")
				: false;
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Use to show more file history list
	 * @param count: Number of file list to add on display
	 */
	loadMoreFileHistory(count: number) {
		this.fileHistoryCount += count;
		this.isFetching = true;
		this.history$ = this.getFiles().pipe(take(1));
	}

	getFiles(): Observable<FileHistory[]> {
		const payGroup = this.payGroup();
		const payCycle = this.payCycle();
		const milestone = this.milestone();

		if (payGroup && payCycle && milestone?.id) {
			const milestoneId = milestone.id;

			return forkJoin([
				this.fileManagerApiService.getFilesG2NFileHistoryByPaygroupIncludingPayCycleID(
					payGroup.id,
					payCycle.id,
					-1,
					0
				),
				this.tppPaymentsApiService.getCancelReport(payGroup.id, milestoneId, 1000, 0),
				this.fileManagerApiService.fetchG2NIntegrationHistory(payCycle.id),
				this.fileManagerApiService.getCPNDeductionFilesHistoryByPaygroupIncludingPayCycleID(
					payGroup.id,
					payCycle.id,
					-1,
					0
				)
			]).pipe(
				takeUntil(this.destroy$),
				map(
					(
						data: [
							FileHistoryPagination,
							cancelReportWithPagination,
							FileManagerPlaceholdersPagination,
							FileHistoryPagination
						]
					) => {
						const [fileHistory, cancelReport, payrollIntegration, deductionFile] = data;

						// Normal History
						const history: FileHistory[] = fileHistory.items;

						// Add deduction file items if available
						if (deductionFile.items.length) {
							deductionFile.items.forEach(item => {
								history.push(item);
							});
						}
						// make deep copy
						// This prevents changes to fileResults from affecting initialFileResults, ensuring that initialFileResults retains the original data.
						const initialFileResults = JSON.parse(JSON.stringify(history));

						// Insert usernames into the 'history' array

						// Milestone Reset History
						for (const resetHistory of milestone.resetHistory) {
							history.push({
								userReset: resetHistory.userId,
								createdAt: resetHistory.resetAt
							});
						}
						// Cancel History
						if (cancelReport.items.length) {
							this.cancelReports = cancelReport.items;

							for (const cancelReport of this.cancelReports) {
								history.push({
									userCancelled: cancelReport.canceledBy,
									createdAt: cancelReport.canceledAt
								});
							}
						}

						// PayRoll placeholder data
						if (payrollIntegration?.items) {
							payrollIntegration?.items.forEach(payroll => {
								if (
									this.fileManagerApiService.isValidIntegrationTaskHistoryId(
										payroll,
										initialFileResults
									)
								) {
									const historyItem: FileHistory = {
										createdAt: new Date(payroll.createdAt),
										fileId: "",
										taskId: "payrollTest",
										fileName: "-",
										fileType: FileTypes.PAYROLL_INTEGRATION,
										status: payroll.status.name,
										integrationErrorMessages: []
									};

									if (!payroll.status.messages.length) {
										historyItem.integrationErrorMessages?.push({
											timeStamp: payroll.createdAt,
											fileName: "Payroll Integration",
											message: ""
										});
									} else {
										this.extractMessageFromError(payroll.status.messages).forEach(message => {
											historyItem.integrationErrorMessages?.push({
												timeStamp: payroll.createdAt,
												fileName: "Payroll Integration",
												message: message
											});
										});
									}

									history.push(historyItem);
								}
							});
						}

						//Sort by date
						const fileResults = history.sort(
							(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
						);

						// Check to see if reset banner should show
						if (fileResults[0]?.userReset) {
							this.resetStatus.emit(true);
							this.lastResetIndex = 0;
						}

						// Check to see if cancel banner should show
						if (fileResults[0]?.userCancelled) {
							this.cancelStatus.emit(true);
							this.lastResetIndex = 0;
						}

						// Check to see from where the files entries should be greyed out
						if (this.lastResetIndex !== 0) {
							let resetFound = false;
							let i = 0;
							while (i < fileResults.length && !resetFound) {
								if (fileResults[i].userReset || fileResults[i].userCancelled) {
									this.lastResetIndex = i;
									resetFound = true;
								}
								i++;
							}
						}

						this.isFetching = false;
						this.currentFileHistoryCount.emit(this.fileHistoryCount);
						this.totalFileHistoryCount.emit(fileResults.length);

						if (this.isPaymentDeductionEnabled) {
							this.fileHistoryItems = fileResults;
							this.checkPaymentOrderUploadButtonStatus();
						}

						return fileResults.filter((f, i) => i < this.fileHistoryCount);
					}
				)
			);
		} else {
			return of([]); // Return an empty observable if milestone.id is not set
		}
	}
	extractMessageFromError(messages: string[]): string[] {
		const defualtMessage =
			"Sorry, it seems the integration you're attempting isn't possible. Please reach out the administrator for assistance";

		const user_friendly_messages: string[] = [];

		if (!messages.length) return [defualtMessage];

		messages.forEach(message => {
			if (message.toLowerCase().includes("there is a transaction on the current run"))
				user_friendly_messages.push("There is a transaction on the current run");
			if (message.toLowerCase().includes("has not been approved by payroll for"))
				user_friendly_messages.push("Run has not been approved by payroll");
			if (message.toLowerCase().includes("payrollcycleid is not informed for"))
				user_friendly_messages.push("The Run ID is not informed");
		});

		if (!user_friendly_messages.length) user_friendly_messages.push(defualtMessage);

		return user_friendly_messages;
	}

	hasRecordOfAutomaticIntegration(fileHistory: FileHistory[]): boolean {
		return fileHistory.filter((file: FileHistory) => !this.isXlsxFile(file.fileName!)).length > 0;
	}

	refreshFiles(): void {
		this.fileHistoryCount = this.defaultFileHistoryCount;
		this.isFetching = true;
		this.history$ = this.getFiles().pipe(take(1));
	}

	isTaskComplete(file: FileHistory): boolean {
		if (file?.taskOverridingStatus) return true;

		const handshake = file?.attributes?.["NET_HANDSHAKE"];
		const isHandshakeSuccessful = handshake?.status === "COMPLETED" && handshake?.allMatch;

		return file?.status === "SUCCESS" && (file?.fileType === "DEDUCTIONS" || isHandshakeSuccessful);
	}

	markTaskComplete(taskId: string): void {
		const dialogRef = this.dialog.open(TaskCompleteDialogComponent, {
			width: "666px",
			height: "441px",
			panelClass: "dialog-container"
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.fileManagerApiService.overrideTaskStatus(taskId, "COMPLETED").subscribe({
					next: () => {
						this.refreshFiles();
					},
					error: () => {
						this.toastService.showError("An error has occured while trying to mark the task as complete");
					}
				});
			}
		});
	}

	viewErrors(file: FileHistory): void {
		const historyLogDatasource: PaymentOrderHistoryLog = {
			fileName: file.fileName,
			fileCreatedAt: file.createdAt,
			fileProcessedAt: file.processedAt,
			taskId: file.taskId,
			errorType: file.fileType,
			payGroup: this.payGroup()!,
			integrationErrorMessages: file.integrationErrorMessages ? file.integrationErrorMessages : [],
			handShakeResults:
				file.attributes && file.attributes!["NET_HANDSHAKE"] ? file.attributes!["NET_HANDSHAKE"] : undefined,
			taskOverridingStatus: file.taskOverridingStatus,
			errorFileType: this.isXlsxFile(file.fileName!)
				? file.fileType === "DEDUCTIONS"
					? FileTypes.DEDUCTIONS
					: FileTypes.G2N
				: FileTypes.PAYROLL_INTEGRATION
		};
		this.onViewHistoryLogClick.emit(historyLogDatasource);
	}

	downloadFile(fileId: string, fileName: string): void {
		this.fileManagerApiService
			.getFileObject(fileId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(buffer => {
				const data: Blob = new Blob([buffer], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				});

				saveAs(data, fileName);
			});
	}

	fileClick() {
		this.onFileClick.emit();
	}

	/**
	 * Emits CPN Deductions file upload
	 */
	uploadCPNDeductionFile() {
		this.cpNowFileCLick.emit();
	}

	/**
	 * Checks Payment Order upload buttons
	 * @param cpnDeductionFile: CP Now file uploaded
	 */
	checkPaymentOrderUploadButtonStatus(): void {
		if (this.fileHistoryItems.length === 0) return;

		const fileItem = this.fileHistoryItems[0];

		if (!fileItem.hasOwnProperty("userReset")) {
			if (fileItem.fileType === "G2N") {
				this.isEnabledCPNDeductionUpload =
					(fileItem.status === "SUCCESS" &&
						fileItem.attributes?.NET_HANDSHAKE?.status === "COMPLETED" &&
						fileItem.attributes?.NET_HANDSHAKE?.allMatch) ||
					fileItem.taskOverridingStatus?.status === "COMPLETED";
			} else if (fileItem.hasOwnProperty("userCancelled")) {
				this.isEnabledG2NFileUpload = true;
			} else {
				this.isEnabledG2NFileUpload = false;
				this.isEnabledCPNDeductionUpload = false;
			}
		} else {
			this.isEnabledCPNDeductionUpload = false;
		}
	}

	runPayrollIntegration(): void {
		const payCycle = this.payCycle();
		if (!payCycle?.id) return;

		this.fileManagerApiService
			.refreshPayrollIntegrationNets(payCycle.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.toastService.showSuccess("Request to trigger payroll integration was sent sucessfully");
					this.history$ = this.getFiles().pipe(take(1));
				},
				error: () => {
					this.toastService.showError(
						"An error has occured while trying to send a request to trigger the payroll integration"
					);
				}
			});
	}

	viewLogs(taskId: string | undefined): void {
		const dialogRef = this.dialog.open(FileErorrDialogComponent, {
			width: "995px",
			height: "570px",
			panelClass: "dialog-container",
			data: {
				taskId: taskId,
				errorType: "G2N",
				payGroup: this.payGroup()
			}
		});

		dialogRef.afterClosed().subscribe();
	}

	isXlsxFile(fileName: string): boolean {
		return fileName.toLowerCase().includes(".xlsx");
	}
}
